import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { buildUrl } from '@utils/url/url.util';
import { Constants } from '@utils/Constants';
import { RESTServerRoute } from '../../types/Server';
import {
  OcpiEndpointDataResult,
  OCPIGenerateEmspTokenResponse,
  OCPIGenerateLocalTokenResponse,
  OCPIOperationResultResponse,
  OCPIPingResponse,
  Ordering,
  Paging,
} from '../../types/DataResult';
import { FilterParams } from '../../types/GlobalType';
import { buildSortingParams } from '../utils/build-sorting-params.utils';
import { buildPagingParams } from '../utils/build-paging-params.utils';
import { OCPIEndpoint } from '../../types/ocpi/OCPIEndpoint';

@Injectable()
export class OcpiApiService {
  public constructor(private httpClient: HttpClient) {}

  public getOcpiEndpoints(
    params: FilterParams,
    paging: Paging = Constants.DEFAULT_PAGING,
    ordering: Ordering[] = [],
  ): Observable<OcpiEndpointDataResult> {
    const queryParams = { ...params, ...buildSortingParams(ordering), ...buildPagingParams(paging) };

    return this.httpClient.get<OcpiEndpointDataResult>(RESTServerRoute.REST_OCPI_ENDPOINTS, {
      params: queryParams,
    });
  }

  public pingOcpiEndpoint(ocpiEndpoint: OCPIEndpoint): Observable<OCPIPingResponse> {
    return this.httpClient.put<OCPIPingResponse>(
      buildUrl(RESTServerRoute.REST_OCPI_ENDPOINT_PING, { id: ocpiEndpoint.id }),
      ocpiEndpoint,
    );
  }

  public createOcpiEndpoint(ocpiEndpoint: any): Observable<void> {
    return this.httpClient.post<void>(RESTServerRoute.VEV_OCPI_ENDPOINTS, ocpiEndpoint);
  }

  public generateEmspToken(): Observable<OCPIGenerateEmspTokenResponse> {
    return this.httpClient.get<OCPIGenerateEmspTokenResponse>(RESTServerRoute.VEV_OCPI_GENERATE_TOKEN);
  }

  public generateCpoToken(endpointName: string): Observable<OCPIGenerateLocalTokenResponse> {
    return this.httpClient.put<OCPIGenerateLocalTokenResponse>(
      RESTServerRoute.REST_OCPI_SERVER_ENDPOINT_GENERATE_TOKEN,
      { name: endpointName },
    );
  }

  public updateOcpiEndpoint(ocpiEndpoint: OCPIEndpoint): Observable<void> {
    return this.httpClient.put<void>(
      buildUrl(RESTServerRoute.VEV_OCPI_ENDPOINT, { id: ocpiEndpoint.id }),
      ocpiEndpoint,
    );
  }

  public registerEndpoint(endpointId: string): Observable<void> {
    return this.httpClient.put<void>(buildUrl(RESTServerRoute.VEV_OCPI_REGISTER_ENDPOINT, { id: endpointId }), {});
  }

  public unregisterOcpiEndpoint(id: string): Observable<void> {
    return this.httpClient.put<void>(buildUrl(RESTServerRoute.VEV_OCPI_UNREGISTER_ENDPOINT, { id }), {});
  }

  public updateEndpointCredentials(endpointId: string): Observable<void> {
    return this.httpClient.put<void>(buildUrl(RESTServerRoute.VEV_OCPI_UPDATE_CREDENTIALS, { id: endpointId }), {});
  }

  public deleteEndpoint(endpointId: string): Observable<void> {
    return this.httpClient.delete<void>(buildUrl(RESTServerRoute.VEV_OCPI_ENDPOINT, { id: endpointId }));
  }

  public pushTokens(endpointId: string): Observable<void> {
    return this.httpClient.put<void>(buildUrl(RESTServerRoute.REST_OCPI_ENDPOINT_PUSH_TOKENS, { id: endpointId }), {});
  }

  public fetchLocations(endpointId: string): Observable<void> {
    return this.httpClient.put<void>(
      buildUrl(RESTServerRoute.REST_OCPI_ENDPOINT_PULL_LOCATIONS, { id: endpointId }),
      {},
    );
  }

  public pullSessionsOcpiEndpoint(endpointId: string): Observable<OCPIOperationResultResponse> {
    return this.httpClient.put<OCPIOperationResultResponse>(
      buildUrl(RESTServerRoute.REST_OCPI_ENDPOINT_PULL_SESSIONS, { id: endpointId }),
      {},
    );
  }

  public pullCdrsOcpiEndpoint(endpointId: string): Observable<OCPIOperationResultResponse> {
    return this.httpClient.put<OCPIOperationResultResponse>(
      buildUrl(RESTServerRoute.REST_OCPI_ENDPOINT_PULL_CDRS, { id: endpointId }),
      {},
    );
  }

  public pullTariffsOcpiEndpoint(endpointId: string): Observable<OCPIOperationResultResponse> {
    return this.httpClient.put<OCPIOperationResultResponse>(
      buildUrl(RESTServerRoute.REST_OCPI_ENDPOINT_PULL_TARIFFS, { id: endpointId }),
      {},
    );
  }

  // CPO SIDE
  public sendEVSEStatusesOcpiEndpoint(endpointId: string): Observable<OCPIOperationResultResponse> {
    return this.httpClient.put<OCPIOperationResultResponse>(
      buildUrl(RESTServerRoute.REST_OCPI_ENDPOINT_SEND_EVSE_STATUSES, { id: endpointId }),
      {},
    );
  }

  public pullTokensOcpiEndpoint(endpointId: string): Observable<OCPIOperationResultResponse> {
    return this.httpClient.put<OCPIOperationResultResponse>(
      buildUrl(RESTServerRoute.REST_OCPI_ENDPOINT_PULL_TOKENS, { id: endpointId }),
      {},
    );
  }

  public pushLocationsOcpiEndpoint(endpointId: string): Observable<OCPIOperationResultResponse> {
    return this.httpClient.put<OCPIOperationResultResponse>(
      buildUrl(RESTServerRoute.REST_OCPI_SERVER_PUSH_LOCATIONS, { id: endpointId }),
      {},
    );
  }

  public checkLocationsOcpiEndpoint(endpointId: string): Observable<OCPIOperationResultResponse> {
    return this.httpClient.put<OCPIOperationResultResponse>(
      buildUrl(RESTServerRoute.REST_OCPI_SERVER_CHECK_LOCATIONS, { id: endpointId }),
      {},
    );
  }

  public checkSessionsOcpiEndpoint(endpointId: string): Observable<OCPIOperationResultResponse> {
    return this.httpClient.put<OCPIOperationResultResponse>(
      buildUrl(RESTServerRoute.REST_OCPI_SERVER_CHECK_SESSIONS, { id: endpointId }),
      {},
    );
  }

  public checkCdrsOcpiEndpoint(endpointId: string): Observable<OCPIOperationResultResponse> {
    return this.httpClient.put<OCPIOperationResultResponse>(
      buildUrl(RESTServerRoute.REST_OCPI_SERVER_CHECK_CDRS, { id: endpointId }),
      {},
    );
  }

  public checkTariffsOcpiEndpoint(endpointId: string): Observable<OCPIOperationResultResponse> {
    return this.httpClient.put<OCPIOperationResultResponse>(
      buildUrl(RESTServerRoute.REST_OCPI_SERVER_CHECK_TARIFFS, { id: endpointId }),
      {},
    );
  }

  public pushTariffsOcpiEndpoint(endpointId: string): Observable<OCPIOperationResultResponse> {
    return this.httpClient.put<OCPIOperationResultResponse>(
      buildUrl(RESTServerRoute.REST_OCPI_SERVER_PUSH_TARIFFS, { id: endpointId }),
      {},
    );
  }

  public pushPeriodDataOcpiEndpoint(endpointId: string): Observable<OCPIOperationResultResponse> {
    // Execute
    return this.httpClient.put<OCPIOperationResultResponse>(
      buildUrl(RESTServerRoute.REST_OCPI_ENDPOINT_PUSH_PERIOD_DATA, { id: endpointId }),
      {},
    );
  }
}
